import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bot, Loader2, Lock, ShieldAlert, Wallet } from 'lucide-react';
import { ProspectShell } from '../../components/prospect/ProspectShell';
import { HealthGauge } from '../../components/ui/HealthGauge';
import type { HealthScore } from '../../types/org.types';
import baloIcon from '../../assets/balo-icon.png';
import cyrilPhoto from '../../assets/cyril-duvignau.jpg';
import { recordBaloClick } from '../../lib/hubApi';

type Severity = 'high' | 'medium' | 'low' | 'info';
type Finding = { id: string; severity: Severity; category: string; title: string; context?: string };
export type Teaser = {
  orgName: string;
  orgType: 'production' | 'sandbox';
  isSandbox: boolean;
  capturedAt: number;
  healthscore: HealthScore | null;
  findings: Finding[];
  orgStats: { label: string; value: string }[];
  orgHealth: OrgHealthTiles | null;
  licenseValue: LicenseValue | null;
  security: SecurityExposure | null;
  agentforce: AgentforceTeaser | null;
  automationDataModel: AutomationDataModel | null;
  baloProfileUrl: string;
};
// Mirrors AutomationDataModel in snapshot.service.ts. Edit both together.
type AutomationDataModel = {
  tech: { label: string; active: number; inactive: number }[] | null;
  objects: { label: string; value: number }[] | null;
};
type AgentforceTeaser = {
  level: 'strong' | 'partial' | 'weak';
  headline: string;
  agents: { name: string; why: string }[];
};
type LicenseValue = {
  idleSeats: { name: string; provisioned: number; used: number; idle: number; utilPct: number | null }[];
  totalIdleSeats: number;
  idleLicenseTypes: number;
  dormantUsers: number | null;
  dormantAnnualCost: number | null;
  currency: string;
  hasUnpriced: boolean;
};
type SecurityExposure = {
  systemAdmins: number;
  customAdminProfiles: number;
  postureScore: number | null;
  postureMax: 25;
};
// Mirrors OrgHealthTiles in snapshot.service.ts — edit both together.
type OrgHealthTiles = {
  apex: {
    asyncApexPct: number | null;
    scheduledJobs: number | null;
    scheduledJobsLimit: number;
    invalidClasses: number | null;
    oldApiClasses: number | null;
  } | null;
  licenses: { name: string; used: number; total: number; utilPct: number | null }[] | null;
  security: {
    score: number;
    band: 'Excellent' | 'Very good' | 'Good' | 'Poor' | 'Very poor';
    highRisks: number | null;
    mediumRisks: number | null;
  } | null;
  access: {
    unassignedPermSets: number | null;
    lowAssignmentPermSets: number | null;
    unassignedRoles: number | null;
    usersWithNoRole: number | null;
  } | null;
};

// Display symbol only — the amount is operator-typed (LICENSE_COSTS_AUD), the
// currency a label (LICENSE_COSTS_CURRENCY). No conversion, no fabricated rate.
const CURRENCY_SYMBOL: Record<string, string> = { AUD: 'A$', SGD: 'S$', USD: 'US$', NZD: 'NZ$', GBP: '£', EUR: '€' };
const money = (amount: number, currency: string): string =>
  `${CURRENCY_SYMBOL[currency] ?? currency + ' '}${amount.toLocaleString()}`;

// Section 11 category weights, shown as sub-score bars beside the /100 gauge.
const CATEGORIES = [
  { key: 'automation', label: 'Automation', max: 25 },
  { key: 'security', label: 'Security', max: 25 },
  { key: 'dataQuality', label: 'Data quality', max: 20 },
  { key: 'metadata', label: 'Metadata', max: 15 },
  { key: 'users', label: 'Users', max: 15 },
] as const;

const SEV_STYLE: Record<Severity, string> = {
  high: 'bg-sunset/15 text-sunset border-sunset/40',
  medium: 'bg-sunrise/15 text-sunrise border-sunrise/40',
  low: 'bg-adopt/15 text-adopt border-adopt/40',
  info: 'bg-surface-3 text-neutral-400 border-border',
};

const POLL_MS = 2500;
const MAX_POLLS = 48; // ~2 min ceiling before we give up on the audit

// The prospect teaser. Two entry points, one page:
//  • /prospect/teaser — post-OAuth, reads the session lead; the background
//    audit may still be running, so it polls until the snapshot lands.
//  • /teaser/:token   — revisit link, resumes from the reusable teaser token.
// Both render purely from the stored snapshot — no Salesforce session.
export function ProspectTeaserPage() {
  const { token } = useParams<{ token?: string }>();
  const [params] = useSearchParams();
  const [teaser, setTeaser] = useState<Teaser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const clean = (s: string | null | undefined): string | undefined =>
      s ? s.replace(/(\\[rn])+$/g, '').trim() : undefined;

    let polls = 0;
    let timer: ReturnType<typeof setTimeout>;

    // 202 = audit still running → poll; 200 = teaser ready; else error.
    const handle = async (res: Response): Promise<void> => {
      if (res.status === 202) {
        if (++polls >= MAX_POLLS) {
          setError('This is taking longer than expected. Refresh in a minute, or use the link we emailed you.');
          return;
        }
        timer = setTimeout(() => void fetchTeaser(), POLL_MS);
        return;
      }
      if (!res.ok) {
        setError('We could not load your results. The link may have expired. Ask Cyril Duvignau for a new one.');
        return;
      }
      setTeaser((await res.json()) as Teaser);
    };

    const fetchTeaser = async (): Promise<void> => {
      try {
        // Revisit link: resume from the token once, then poll the plain
        // endpoint if (rarely) the snapshot isn't written yet.
        const res =
          token && polls === 0
            ? await fetch('/api/prospect/teaser/resume', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: clean(token), lead: clean(params.get('lead')) }),
              })
            : await fetch('/api/prospect/teaser');
        await handle(res);
      } catch {
        setError('We could not reach the server. Check your connection and refresh.');
      }
    };

    void fetchTeaser();
    return () => clearTimeout(timer);
  }, [token, params]);

  if (error) {
    return (
      <ProspectShell>
        <div className="min-h-[50vh] flex items-center justify-center">
          <div className="max-w-md text-center px-6">
            <h1 className="text-2xl font-semibold mb-3">Hang tight.</h1>
            <p className="text-sm text-neutral-400 leading-relaxed">{error}</p>
          </div>
        </div>
      </ProspectShell>
    );
  }

  if (!teaser) {
    return (
      <ProspectShell>
        <div className="min-h-[50vh] flex flex-col items-center justify-center gap-4 text-center px-6">
          <Loader2 className="animate-spin text-grow" size={28} />
          <div>
            <p className="text-crux-light font-medium">Auditing your org…</p>
            <p className="text-sm text-neutral-500 mt-1">
              Scanning objects, flows, users, permissions and more. 20 to 45 seconds.
            </p>
          </div>
        </div>
      </ProspectShell>
    );
  }

  return <TeaserView teaser={teaser} />;
}

// Presentational body — a pure function of the projected snapshot. Split out
// so /prospect/teaser-preview (dev only) can render it against mock data
// without the OAuth/poll round-trip.
export function TeaserView({ teaser }: { teaser: Teaser }) {
  const hs = teaser.healthscore;
  // The expert card is the page's only call to action. With no Balo profile
  // configured it is hidden, and every "on the call" caption below would be a
  // promise the page cannot keep.
  const hasCall = Boolean(teaser.baloProfileUrl);

  return (
    <ProspectShell>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="max-w-[880px] mx-auto py-10"
      >
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-grow mb-3">
          Your org audit
        </div>
        <h1 className="text-[34px] font-bold tracking-[-0.03em] leading-none">{teaser.orgName}</h1>
        {teaser.isSandbox ? (
          <p className="mt-3 text-[13px] text-sunrise bg-sunrise/10 border border-sunrise/30 px-3 py-2 inline-block">
            You connected a <strong>sandbox</strong>. These results reflect that environment, not your
            production org.
          </p>
        ) : null}

        {/* Score + sub-scores */}
        <div className="mt-8 grid md:grid-cols-2 gap-8 items-center bg-surface-2 border border-border p-8">
          <div className="flex justify-center">
            {hs ? (
              <HealthGauge score={hs} />
            ) : (
              <p className="text-neutral-500 text-sm">Health score unavailable for this org.</p>
            )}
          </div>
          {hs ? (
            <div className="space-y-3">
              {CATEGORIES.map((c) => {
                const val = hs.breakdown[c.key];
                const pct = Math.round((val / c.max) * 100);
                return (
                  <div key={c.key}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-neutral-300">{c.label}</span>
                      <span className="tabular-nums text-neutral-400">
                        {val}/{c.max}
                      </span>
                    </div>
                    <div className="h-1.5 bg-surface-3">
                      <div
                        className="h-full bg-grow"
                        style={{ width: `${pct}%`, background: pct >= 70 ? '#3FA96B' : pct >= 45 ? '#C7851A' : '#D64545' }}
                      />
                    </div>
                  </div>
                );
              })}
              {hs.foundations.eligible && hs.foundations.score !== null ? (
                <p className="text-[11px] text-neutral-500 pt-1">
                  Foundations AI utilisation: {hs.foundations.score}/15
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Aggregate org facts — proof we actually scanned */}
        {teaser.orgStats.length ? (
          <div
            className={`mt-6 grid grid-cols-2 sm:grid-cols-3 ${
              teaser.orgStats.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-4'
            } gap-px bg-border border border-border`}
          >
            {teaser.orgStats.map((s) => (
              <div key={s.label} className="bg-surface-2 px-4 py-3">
                <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-neutral-500">
                  {s.label}
                </div>
                <div className="text-sm text-crux-light font-medium mt-1 tabular-nums">{s.value}</div>
              </div>
            ))}
          </div>
        ) : null}

        {/* The four Setup "Org Health and Usage" tiles Salesforce gates behind
            Agentforce, reproduced read-only. Counts in the clear, named lists
            behind the call. */}
        {teaser.orgHealth ? <OrgHealthSection oh={teaser.orgHealth} hasCall={hasCall} /> : null}

        {/* Headline findings */}
        {teaser.findings.length ? (
          <div className="mt-8">
            <h2 className="text-lg font-semibold mb-3">What stood out</h2>
            <div className="space-y-2">
              {teaser.findings.map((f) => (
                <div key={f.id} className="flex items-start gap-3 bg-surface-2 border border-border p-4">
                  <span
                    className={`shrink-0 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 border ${SEV_STYLE[f.severity]}`}
                  >
                    {f.severity}
                  </span>
                  <div>
                    <div className="text-sm text-crux-light font-medium">{f.title}</div>
                    {f.context ? <div className="text-xs text-neutral-500 mt-0.5">{f.context}</div> : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Value for money — idle seats + dormant assigned users. Problem/cost
            only; the fix is behind the lock. */}
        {teaser.licenseValue ? <LicenseValueCard lv={teaser.licenseValue} /> : null}

        {/* Security exposure — privileged access. Problem only; remediation locked. */}
        {teaser.security ? <SecurityCard sec={teaser.security} /> : null}

        {/* Agentforce opportunity — which agents fit, grounded in the metadata.
            Opportunity, not problem; the scope stays behind the call. */}
        {teaser.agentforce ? <AgentforceCard af={teaser.agentforce} hasCall={hasCall} /> : null}

        {/* Automation and data model, counts only. No object or item names
            reach this payload. The expert card below is the only call to
            action on this page (TYR-209). */}
        {teaser.automationDataModel ? (
          <AutomationDataModelSection adm={teaser.automationDataModel} />
        ) : null}
        {teaser.baloProfileUrl ? <ExpertCard url={teaser.baloProfileUrl} /> : null}

        <DeleteScanData />
      </motion.div>
    </ProspectShell>
  );
}

// The privacy page promises a self-serve delete; this is it.
function DeleteScanData() {
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle');
  const onDelete = async () => {
    if (!window.confirm('Delete your scan results and revisit links? This cannot be undone.')) return;
    setState('busy');
    try {
      const res = await fetch('/api/prospect/teaser', { method: 'DELETE' });
      setState(res.ok ? 'done' : 'error');
    } catch {
      setState('error');
    }
  };
  if (state === 'done') {
    return <p className="mt-8 text-[12px] text-neutral-500">Your scan results are deleted. You can close this page.</p>;
  }
  return (
    <p className="mt-8 text-[12px] text-neutral-500">
      We keep these results for 6 months.{' '}
      <button
        type="button"
        onClick={onDelete}
        disabled={state === 'busy'}
        className="underline hover:text-crux-light transition-colors disabled:opacity-50"
      >
        Delete my scan results now
      </button>
      {state === 'error' && <span className="text-sunset"> Delete failed. Reload the page and try again.</span>}
    </p>
  );
}

// Two plain count tables. Labels and numbers only, per the v2.4 copy rule.
function AutomationDataModelSection({ adm }: { adm: AutomationDataModel }) {
  return (
    <div className="mt-8">
      <h2 className="text-lg font-semibold mb-3">Automation and data model</h2>
      <div className="grid md:grid-cols-2 gap-4">
        {adm.tech ? (
          <table className="w-full border border-border bg-surface-2 text-sm">
            <thead>
              <tr className="text-[10px] font-semibold uppercase tracking-[0.1em] text-neutral-500">
                <th className="text-left px-4 py-2 border-b border-border">Automation</th>
                <th className="text-right px-4 py-2 border-b border-border">Active</th>
                <th className="text-right px-4 py-2 border-b border-border">Inactive</th>
              </tr>
            </thead>
            <tbody>
              {adm.tech.map((r) => (
                <tr key={r.label} className="border-b border-border last:border-0">
                  <td className="px-4 py-2 text-crux-light">{r.label}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-crux-light">{r.active}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-neutral-400">{r.inactive}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {adm.objects ? (
          <table className="w-full border border-border bg-surface-2 text-sm self-start">
            <thead>
              <tr className="text-[10px] font-semibold uppercase tracking-[0.1em] text-neutral-500">
                <th className="text-left px-4 py-2 border-b border-border">Objects</th>
                <th className="text-right px-4 py-2 border-b border-border">Count</th>
              </tr>
            </thead>
            <tbody>
              {adm.objects.map((r) => (
                <tr key={r.label} className="border-b border-border last:border-0">
                  <td className="px-4 py-2 text-crux-light">{r.label}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-crux-light">{r.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>
    </div>
  );
}

// The expert card. Balo is a supporting partner, so its mark is large and
// sits beside the name. Both actions open the same public profile.
function ExpertCard({ url }: { url: string }) {
  return (
    <div className="mt-4 max-w-[560px] border border-border bg-surface-2 grid sm:grid-cols-[150px_1fr]">
      <img
        src={cyrilPhoto}
        alt="Cyril Duvignau"
        className="w-full h-full object-cover block min-h-[170px] bg-surface-3"
      />
      <div className="p-4 sm:p-5">
        <div className="flex justify-between items-start gap-3">
          <div>
            <div className="text-[17px] font-bold text-crux-light leading-tight">Cyril Duvignau</div>
            <div className="text-xs text-neutral-500 mt-0.5">TYRL · Australia · Salesforce expert</div>
          </div>
          <span className="flex items-center gap-2.5 text-[26px] font-extrabold tracking-[-0.02em] text-crux-light">
            <img src={baloIcon} alt="" className="w-16 h-16 object-contain" />
            Balo
          </span>
        </div>
        <div className="flex gap-2 mt-4 flex-wrap">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => recordBaloClick('/api/prospect/balo-click')}
            className="flex-1 text-center h-10 leading-10 px-4 bg-sfblue hover:opacity-90 text-white font-semibold text-sm transition-opacity"
          >
            Book a call
          </a>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => recordBaloClick('/api/prospect/balo-click')}
            className="flex-1 text-center h-10 leading-10 px-4 border border-border text-crux-light hover:bg-surface-3 font-semibold text-sm transition-colors"
          >
            Request a quote
          </a>
        </div>
      </div>
    </div>
  );
}

// Value-for-money: what the org is provisioned-and-billed for but doesn't use.
// Idle seats always (units); the dormant-user AUD figure only when configured.
// Problem/cost only — no remediation, no operator instructions on a prospect page.
function LicenseValueCard({ lv }: { lv: LicenseValue }) {
  const cost = lv.dormantAnnualCost !== null ? money(lv.dormantAnnualCost, lv.currency) : null;
  const headlineCount = (lv.totalIdleSeats || lv.dormantUsers || 0).toLocaleString();
  return (
    <div className="mt-8 bg-surface-2 border border-border p-8">
      <div className="flex items-center gap-2 text-neutral-400 mb-2">
        <Wallet size={15} />
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em]">Value for money</span>
      </div>
      <h2 className="text-xl font-bold tracking-[-0.02em]">
        {cost
          ? `Around ${cost}/yr in licences that aren't returning value`
          : `${headlineCount} provisioned seats sitting unused`}
      </h2>
      <p className="text-sm text-neutral-400 mt-2 max-w-xl leading-relaxed">
        {cost
          ? "You're provisioned and billed for more than the org actually uses."
          : "You're provisioned for more seats than the org actually uses."}
      </p>

      {lv.idleSeats.length ? (
        <div className="mt-5 space-y-2.5">
          {lv.idleSeats.map((s) => {
            const usedPct = s.provisioned > 0 ? Math.round((s.used / s.provisioned) * 100) : 0;
            return (
              <div key={s.name}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-neutral-300">{s.name}</span>
                  <span className="tabular-nums text-neutral-400">
                    {s.idle.toLocaleString()} idle of {s.provisioned.toLocaleString()}
                  </span>
                </div>
                <div className="h-1.5 bg-surface-3">
                  <div className="h-full bg-grow" style={{ width: `${usedPct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {lv.dormantUsers ? (
        <p className="mt-5 text-sm text-crux-light">
          <span className="tabular-nums font-semibold">{lv.dormantUsers.toLocaleString()}</span> assigned users
          haven't logged in for 90+ days
          {cost ? <>, about {cost}/yr at your configured rates</> : null}.
          {cost && lv.hasUnpriced ? (
            <span className="text-neutral-500"> More sit on licence types not yet priced, so the real figure is higher.</span>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}

// Privileged-access exposure. Flagship = admin users; custom admin profiles as a
// supporting fact (never summed with users). Problem only — the fix is locked.
function SecurityCard({ sec }: { sec: SecurityExposure }) {
  const admins = sec.systemAdmins;
  const headline =
    admins >= 1
      ? `${admins} user${admins === 1 ? '' : 's'} hold full System Administrator`
      : `${sec.customAdminProfiles} custom profiles grant admin-level access`;
  return (
    <div className="mt-8 bg-surface-2 border border-border p-8">
      <div className="flex items-center gap-2 text-sunset mb-2">
        <ShieldAlert size={15} />
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em]">Security exposure</span>
      </div>
      <h2 className="text-xl font-bold tracking-[-0.02em]">{headline}</h2>
      <p className="text-sm text-neutral-400 mt-2 max-w-xl leading-relaxed">
        Each has unrestricted read, write, delete and export across the entire org. Best practice keeps System
        Administrator to one or two named accounts. Every extra holder widens the blast radius of a mistake or a
        compromised login.
      </p>
      {admins >= 1 && sec.customAdminProfiles > 0 ? (
        <p className="text-sm text-neutral-400 mt-3">
          <span className="tabular-nums text-crux-light font-semibold">{sec.customAdminProfiles}</span> custom profiles
          also grant admin-level access.
        </p>
      ) : null}
      {sec.postureScore !== null ? (
        <p className="text-[11px] text-neutral-500 pt-4">
          Security posture scored {sec.postureScore}/{sec.postureMax}.
        </p>
      ) : null}
    </div>
  );
}

// Agentforce opportunity. The one forward-looking (not problem) card: names the
// agents that fit and one metadata-grounded reason each. The full readiness
// breakdown and scope stay behind the call, same lock philosophy as the rest.
const AF_CHIP: Record<AgentforceTeaser['level'], { label: string; cls: string }> = {
  strong: { label: 'Strong fit', cls: 'bg-grow/15 text-grow border-grow/40' },
  partial: { label: 'Solid fit', cls: 'bg-sunrise/15 text-sunrise border-sunrise/40' },
  weak: { label: 'Start with a copilot', cls: 'bg-surface-3 text-neutral-400 border-border' },
};

function AgentforceCard({ af, hasCall }: { af: AgentforceTeaser; hasCall: boolean }) {
  const chip = AF_CHIP[af.level];
  return (
    <div className="mt-8 bg-surface-2 border border-border p-8">
      <div className="flex items-center gap-2 text-grow mb-2">
        <Bot size={15} />
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em]">Agentforce opportunity</span>
        <span className={`ml-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 border ${chip.cls}`}>
          {chip.label}
        </span>
      </div>
      <h2 className="text-xl font-bold tracking-[-0.02em]">{af.headline}</h2>
      <p className="text-sm text-neutral-400 mt-2 max-w-xl leading-relaxed">
        Based on your metadata, {af.agents.length === 1 ? 'one agent fits' : 'two agents fit'} your org today:
      </p>
      <div className="mt-5 space-y-3">
        {af.agents.map((a) => (
          <div key={a.name} className="border-l-2 border-grow/40 pl-4">
            <div className="text-sm text-crux-light font-medium">{a.name}</div>
            <div className="text-xs text-neutral-500 mt-0.5 leading-relaxed">{a.why}</div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-neutral-500 pt-5">
        {hasCall
          ? 'The full readiness breakdown, the prerequisites and the scope, we walk through live on the call.'
          : 'The full readiness breakdown, the prerequisites and the scope are in the full report.'}
      </p>
    </div>
  );
}

// ─── Org Health and Usage tiles ──────────────────────────────────────────────
// The four tiles from Salesforce's Setup dashboard (Apex Health, Most Used
// Licenses, Security Health Score, User Access Health), reproduced from the
// read-only snapshot. Per-metric null = row hides; per-tile null = tile hides.
// Counts stay in the clear; every named list is behind the call.

type ChipTone = 'bad' | 'warn' | 'ok' | 'muted';
const CHIP_STYLE: Record<ChipTone, string> = {
  bad: 'bg-sunset/15 text-sunset border-sunset/40',
  warn: 'bg-sunrise/15 text-sunrise border-sunrise/40',
  ok: 'bg-grow/15 text-grow border-grow/40',
  muted: 'bg-surface-3 text-neutral-400 border-border',
};
const VALUE_TONE: Record<ChipTone, string> = {
  bad: 'text-sunset',
  warn: 'text-sunrise',
  ok: 'text-grow',
  muted: 'text-crux-light',
};
const BAR_COLOR: Record<ChipTone, string> = {
  bad: '#D64545',
  warn: '#C7851A',
  ok: '#3FA96B',
  muted: '#C4C2BA',
};

function Tile({
  name,
  chip,
  lock,
  children,
}: {
  name: string;
  chip?: { label: string; tone: ChipTone };
  lock: string;
  children: ReactNode;
}) {
  return (
    <div className="bg-surface-2 border border-border flex flex-col">
      <div className="flex items-center justify-between gap-2 px-5 py-3.5 border-b border-border-subtle">
        <span className="text-[13px] font-bold tracking-[-0.01em] text-crux-light">{name}</span>
        {chip ? (
          <span
            className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 border ${CHIP_STYLE[chip.tone]}`}
          >
            {chip.label}
          </span>
        ) : null}
      </div>
      <div className="flex-1 px-5 py-4 space-y-3.5">{children}</div>
      <div className="flex items-center justify-between gap-2 px-5 py-2.5 border-t border-border-subtle">
        <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-neutral-500">
          Read only via API
        </span>
        <span className="flex items-center gap-1.5 text-[11px] font-medium text-sfblue">
          <Lock size={11} />
          {lock}
        </span>
      </div>
    </div>
  );
}

// One metric line: label left, value right, optional utilisation bar under it.
function MetricRow({
  label,
  value,
  tone = 'muted',
  pct,
}: {
  label: string;
  value: string;
  tone?: ChipTone;
  pct?: number; // 0 to 100 → renders the bar
}) {
  return (
    <div>
      <div className="flex justify-between items-baseline gap-3 text-xs">
        <span className="text-neutral-400">{label}</span>
        <span className={`font-semibold tabular-nums whitespace-nowrap ${VALUE_TONE[tone]}`}>{value}</span>
      </div>
      {pct !== undefined ? (
        <div className="h-1.5 bg-surface-3 mt-1.5">
          <div
            className="h-full"
            style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: BAR_COLOR[tone] }}
          />
        </div>
      ) : null}
    </div>
  );
}

const usageTone = (pct: number): ChipTone => (pct >= 90 ? 'bad' : pct >= 70 ? 'warn' : 'ok');

// A tile caption that says "on the call" promises a conversation. The only way
// to start one on this page is the expert card, and that card is hidden when no
// Balo profile is configured. With no card, promise nothing (TYR-209).
const lockCaption = (detail: string, hasCall: boolean): string =>
  hasCall ? `${detail} on the call` : 'Detail in the full report';

function ApexTile({ apex, hasCall }: { apex: NonNullable<OrgHealthTiles['apex']>; hasCall: boolean }) {
  const chip =
    apex.invalidClasses !== null && apex.invalidClasses > 0
      ? { label: `${apex.invalidClasses} invalid`, tone: 'bad' as const }
      : apex.oldApiClasses !== null && apex.oldApiClasses > 0
        ? { label: `${apex.oldApiClasses} outdated`, tone: 'warn' as const }
        : apex.invalidClasses !== null && apex.oldApiClasses !== null
          ? { label: 'Healthy', tone: 'ok' as const }
          : undefined;
  return (
    <Tile name="Apex Health" chip={chip} lock={lockCaption('Named classes', hasCall)}>
      {apex.asyncApexPct !== null ? (
        <MetricRow
          label="Async Apex consumption (24h limit)"
          value={`${apex.asyncApexPct}%`}
          tone={usageTone(apex.asyncApexPct)}
          pct={apex.asyncApexPct}
        />
      ) : null}
      {apex.scheduledJobs !== null ? (
        <MetricRow
          label={`Scheduled jobs (limit ${apex.scheduledJobsLimit})`}
          value={apex.scheduledJobs.toLocaleString()}
          tone={usageTone((apex.scheduledJobs / apex.scheduledJobsLimit) * 100)}
          pct={(apex.scheduledJobs / apex.scheduledJobsLimit) * 100}
        />
      ) : null}
      {apex.invalidClasses !== null ? (
        <MetricRow
          label="Invalid Apex classes"
          value={apex.invalidClasses.toLocaleString()}
          tone={apex.invalidClasses > 0 ? 'bad' : 'ok'}
        />
      ) : null}
      {apex.oldApiClasses !== null ? (
        <MetricRow
          label="Classes on old API versions"
          value={apex.oldApiClasses.toLocaleString()}
          tone={apex.oldApiClasses > 0 ? 'warn' : 'ok'}
        />
      ) : null}
    </Tile>
  );
}

function LicensesTile({
  rows,
  hasCall,
}: {
  rows: NonNullable<OrgHealthTiles['licenses']>;
  hasCall: boolean;
}) {
  const atCap = rows.filter((r) => (r.utilPct ?? 0) >= 95).length;
  return (
    <Tile
      name="Most Used Licenses"
      chip={atCap > 0 ? { label: `${atCap} at capacity`, tone: 'warn' } : undefined}
      lock={lockCaption('Seat by seat detail', hasCall)}
    >
      {rows.map((r) => (
        <MetricRow
          key={r.name}
          label={r.name}
          value={
            r.utilPct !== null
              ? `${r.used.toLocaleString()} / ${r.total.toLocaleString()} · ${Math.round(r.utilPct)}%`
              : `${r.used.toLocaleString()} / ${r.total.toLocaleString()}`
          }
          tone={(r.utilPct ?? 0) >= 95 ? 'bad' : (r.utilPct ?? 0) >= 10 ? 'ok' : 'muted'}
          pct={r.utilPct ?? undefined}
        />
      ))}
    </Tile>
  );
}

function SecurityScoreTile({ sec }: { sec: NonNullable<OrgHealthTiles['security']> }) {
  const tone: ChipTone = sec.score >= 70 ? 'ok' : sec.score >= 55 ? 'warn' : 'bad';
  const C = 2 * Math.PI * 44;
  const risks =
    sec.highRisks !== null
      ? sec.highRisks > 0
        ? `${sec.highRisks} high risk${sec.mediumRisks ? ` and ${sec.mediumRisks} medium risk` : ''} settings open.`
        : 'No high risk settings open.'
      : null;
  return (
    <Tile name="Security Health Score" chip={{ label: sec.band, tone }} lock="The settings behind the score">
      <div className="flex items-center gap-5">
        <svg width="104" height="104" viewBox="0 0 110 110" role="img" aria-label={`Security Health Check score ${sec.score} percent, ${sec.band}`}>
          <circle cx="55" cy="55" r="44" fill="none" stroke="#E0DED7" strokeWidth="11" />
          <circle
            cx="55"
            cy="55"
            r="44"
            fill="none"
            stroke={BAR_COLOR[tone]}
            strokeWidth="11"
            strokeDasharray={C}
            strokeDashoffset={C * (1 - sec.score / 100)}
            transform="rotate(-90 55 55)"
          />
          <text x="55" y="63" textAnchor="middle" fontSize="24" fontWeight="800" fill="#1B1B1B">
            {sec.score}%
          </text>
        </svg>
        <div>
          <div className={`text-xl font-bold tracking-[-0.02em] ${VALUE_TONE[tone]}`}>{sec.band}</div>
          <p className="text-[11px] text-neutral-500 mt-1.5 leading-relaxed">
            Salesforce's own Health Check baseline, the score Setup shows your admin.
            {risks ? ` ${risks}` : ''}
          </p>
        </div>
      </div>
    </Tile>
  );
}

function AccessTile({
  access,
  hasCall,
}: {
  access: NonNullable<OrgHealthTiles['access']>;
  hasCall: boolean;
}) {
  const sprawl = (access.unassignedPermSets ?? 0) + (access.unassignedRoles ?? 0);
  const chip =
    sprawl > 0
      ? { label: 'Sprawl', tone: 'warn' as const }
      : access.unassignedPermSets !== null && access.unassignedRoles !== null
        ? { label: 'Tidy', tone: 'ok' as const }
        : undefined;
  return (
    <Tile name="User Access Health" chip={chip} lock={lockCaption('Cleanup order', hasCall)}>
      {access.unassignedPermSets !== null ? (
        <MetricRow
          label="Custom permission sets with no users"
          value={access.unassignedPermSets.toLocaleString()}
          tone={access.unassignedPermSets > 0 ? 'warn' : 'ok'}
        />
      ) : null}
      {access.lowAssignmentPermSets !== null ? (
        <MetricRow
          label="Custom permission sets under 10 users"
          value={access.lowAssignmentPermSets.toLocaleString()}
        />
      ) : null}
      {access.unassignedRoles !== null ? (
        <MetricRow
          label="Unassigned roles"
          value={access.unassignedRoles.toLocaleString()}
          tone={access.unassignedRoles > 0 ? 'warn' : 'ok'}
        />
      ) : null}
      {access.usersWithNoRole !== null ? (
        <MetricRow
          label="Active users with no role"
          value={access.usersWithNoRole.toLocaleString()}
          tone={access.usersWithNoRole > 0 ? 'warn' : 'ok'}
        />
      ) : null}
    </Tile>
  );
}

function OrgHealthSection({ oh, hasCall }: { oh: OrgHealthTiles; hasCall: boolean }) {
  return (
    <div className="mt-8">
      <h2 className="text-lg font-semibold">Org health and usage</h2>
      <p className="text-[13px] text-neutral-500 mt-1 max-w-xl leading-relaxed">
        Salesforce reserves this Setup dashboard for orgs already running Agentforce. We read the
        same numbers through the API. Nothing installed, nothing changed.
      </p>
      <div className="mt-4 grid md:grid-cols-2 gap-4">
        {oh.apex ? <ApexTile apex={oh.apex} hasCall={hasCall} /> : null}
        {oh.licenses ? <LicensesTile rows={oh.licenses} hasCall={hasCall} /> : null}
        {oh.security ? <SecurityScoreTile sec={oh.security} /> : null}
        {oh.access ? <AccessTile access={oh.access} hasCall={hasCall} /> : null}
      </div>
    </div>
  );
}
